import React, { useRef, useState } from "react";
import { Field, ErrorMessage } from "formik";
import { get } from "lodash";
import { IMAGE_FETCH_URL, IMAGE_URL } from "../../config";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import {
  UploadCloud,
  X,
  Eye,
  FileText,
  Film,
  Image as ImageIcon,
  ExternalLink,
  Trash2,
  Loader2,
} from "lucide-react";

export const FormFileUploadField = ({
  label = "Upload File",
  name = "fileField",
  labelClassName = "",
  className = "",
  previewClassName = "",
  accept = "*",
  maxSize = 5,
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [previewMedia, setPreviewMedia] = useState(null);

  const inputRef = useRef(null);

  // Helper to determine file type category
  const getFileType = (fileName = "", url = "") => {
    const ext = (fileName || url).split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
      return "image";
    if (["mp4", "webm", "ogg", "mov"].includes(ext)) return "video";
    return ext || "file";
  };

  const uploadFile = (file, form, name) => {
    if (!file) return;

    // Client-side file size validation check
    if (file.size > maxSize * 1024 * 1024) {
      alert(`File size exceeds the limit of ${maxSize}MB.`);
      return;
    }

    setUploadingFileName(file.name);
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onload = () => {
      setIsUploading(false);
      setUploadingFileName("");

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          const filePath = response?.data?.filePath || response?.filePath;

          if (!filePath) {
            throw new Error("Invalid response format");
          }

          const fullUrl = filePath.startsWith("http")
            ? filePath
            : `${IMAGE_FETCH_URL}${filePath}`;

          form.setFieldValue(name, {
            url: fullUrl,
            name: file.name,
            size: file.size,
            type: file.type,
          });
          form.setFieldTouched(name, true, false);
        } catch (err) {
          alert("Failed to parse file upload response!");
        }
      } else {
        alert("File upload failed!");
      }

      if (inputRef.current) inputRef.current.value = "";
    };

    xhr.onerror = () => {
      setIsUploading(false);
      setUploadingFileName("");
      alert("An error occurred during upload!");
      if (inputRef.current) inputRef.current.value = "";
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files?.[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer?.files?.[0];
    uploadFile(file, form, name);
  };

  const handleRemoveFile = (form, name) => {
    form.setFieldValue(name, null);
    form.setFieldTouched(name, true, false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const fileData = get(form.values, name);
        const error = meta.touched && meta.error;
        const fileCategory = fileData
          ? getFileType(fileData.name, fileData.url)
          : null;

        return (
          <div className={`flex flex-col gap-1.5 w-full ${className}`}>
            {label && (
              <label
                className={`text-sm font-semibold text-gray-800 capitalize ${
                  error ? "text-red-500" : labelClassName
                }`}
              >
                {formatUnderscoreToSpace(label)}
              </label>
            )}

            {/* Uploaded File Preview Card */}
            {fileData && !isUploading ? (
              <div
                className={`relative group rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition-all hover:shadow-md ${previewClassName}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Media Thumbnail or Dynamic File Icon */}
                    <div className="relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                      {fileCategory === "image" ? (
                        <img
                          src={fileData.url}
                          alt={fileData.name}
                          className="w-full h-full object-cover"
                        />
                      ) : fileCategory === "video" ? (
                        <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                          <video
                            src={fileData.url}
                            className="w-full h-full object-cover opacity-60"
                          />
                          <Film className="absolute w-5 h-5 text-white" />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="w-5 h-5 text-primary mb-0.5" />
                          <span className="text-[9px] font-bold uppercase text-gray-500 max-w-[36px] truncate">
                            {fileCategory}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* File Details */}
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-xs font-semibold text-gray-800 truncate"
                        title={fileData.name}
                      >
                        {fileData.name}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {fileData.size
                          ? formatFileSize(fileData.size)
                          : "Uploaded file"}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {(fileCategory === "image" || fileCategory === "video") && (
                      <button
                        type="button"
                        onClick={() =>
                          setPreviewMedia({
                            url: fileData.url,
                            type: fileCategory,
                            name: fileData.name,
                          })
                        }
                        className="p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-indigo-50 transition"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}

                    <a
                      href={fileData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-gray-500 hover:text-primary hover:bg-indigo-50 transition"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>

                    <button
                      type="button"
                      onClick={() => handleRemoveFile(form, name)}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                      title="Remove file"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Dropzone & Upload State Container */
              <div
                className={`relative rounded-xl border-2 border-dashed p-6 transition-all cursor-pointer min-h-[160px] flex flex-col justify-center items-center ${
                  isDragActive
                    ? "border-indigo-500 bg-indigo-50/50"
                    : "border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-gray-100/50"
                } ${error ? "border-red-300 bg-red-50/20" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
                onClick={() => inputRef.current?.click()}
              >
                {/* Upload Progress Display */}
                {isUploading ? (
                  <div className="flex flex-col items-center justify-center gap-2.5 w-full max-w-xs">
                    <div className="relative flex items-center justify-center">
                      <Loader2 className="w-7 h-7 animate-spin text-primary" />
                      <span className="absolute text-[10px] font-bold text-gray-700">
                        {uploadProgress}%
                      </span>
                    </div>

                    <p className="text-xs font-medium text-gray-700 truncate max-w-full">
                      Uploading {uploadingFileName}...
                    </p>

                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-200"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  /* Empty State View */
                  <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-primary mb-2">
                      <UploadCloud className="w-5 h-5" />
                    </div>
                    <p className="text-xs font-semibold text-gray-700">
                      Click to upload or drag & drop
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Up to {maxSize}MB
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  className="hidden"
                  onChange={(event) => handleFileSelect(event, form, name)}
                />
              </div>
            )}

            {/* Lightbox Preview Modal */}
            {previewMedia && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
                onClick={() => setPreviewMedia(null)}
              >
                <div
                  className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setPreviewMedia(null)}
                    className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  {previewMedia.type === "video" ? (
                    <video
                      src={previewMedia.url}
                      controls
                      autoPlay
                      className="max-h-[85vh] max-w-full rounded-lg"
                    />
                  ) : (
                    <img
                      src={previewMedia.url}
                      alt={previewMedia.name || "Preview"}
                      className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Validation Error Message */}
            <div className="min-h-[1.25rem]">
              <ErrorMessage name={name}>
                {(msg) => (
                  <span className="block text-xs font-medium text-red-500">
                    {msg}
                  </span>
                )}
              </ErrorMessage>
            </div>
          </div>
        );
      }}
    </Field>
  );
};
